import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/i18n/format';
import { useGetNotesQuery, NoteFilter, NoteTypeEnum } from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';

// Dummy notes for preview mode
const dummyNotes = [
  {
    id: 'n1',
    title: 'Account Intelligence',
    content: '## Account Intelligence\n\n### Company Signals\n- Recently raised Series C ($120M) — expanding engineering team\n- Opened new office in Austin, TX\n- Hiring heavily for DevOps and Platform roles',
    date: 'Feb 27, 2026',
    type: 'ai' as const,
  },
  {
    id: 'n2',
    title: 'Internal Notes',
    content: '## Internal Notes\n\n- Champion is Sarah Chen (VP Eng) — very engaged\n- Budget cycle ends in March, push for Q1 close',
    date: 'Feb 20, 2026',
    type: 'manual' as const,
  },
];

interface UseAccountNotesOptions {
  skip?: boolean; // Skip auto-fetch (e.g., when parent component is not visible)
}

/**
 * Hook for fetching notes associated with an account/company
 * Supports Lovable Preview mode with dummy data
 */
export const useAccountNotes = (accountId: string, options: UseAccountNotesOptions = {}) => {
  const { skip = false } = options;
  const workspaceId = useWorkspaceId();
  // Kept in the memo deps below so a language switch re-renders the note dates.
  const { i18n } = useTranslation();
  const language = i18n.language;

  const filter: NoteFilter = useMemo(
    () => ({
      workspaceId: workspaceId || undefined,
      accountId: accountId,
      deleted: false,
    }),
    [workspaceId, accountId]
  );

  const { data, loading, error, refetch } = useGetNotesQuery({
    variables: {
      filter,
      first: 100, // Load all notes for now
    },
    skip: skip || isLovablePreview() || !workspaceId || !accountId,
    fetchPolicy: 'cache-and-network',
  });

  const notes = useMemo(() => {
    if (isLovablePreview()) {
      return dummyNotes;
    }

    return (
      data?.notes?.edges
        // Tasks are notes with type=task and render in the account's separate
        // Tasks tab — drop them here so a task doesn't appear in both places.
        // Filter client-side (only explicit tasks) rather than adding a
        // type=note server filter, which would also hide legacy notes whose
        // `type` isn't indexed. Mirrors use-deal-notes.
        ?.filter((edge) => edge.node?.type !== NoteTypeEnum.Task)
        .map((edge) => ({
        id: edge.node?.id || '',
        title: edge.node?.title || 'Untitled Note',
        content: (edge.node as any).blocks || '',
        date: formatDate(edge.node?.createdAt, language),
        type: edge.node?.aiGenerated ? ('ai' as const) : ('manual' as const),
      })) || []
    );
  }, [data, language]);

  return {
    notes,
    loading: isLovablePreview() ? false : loading,
    error: isLovablePreview() ? null : error,
    refetch: isLovablePreview() ? (() => Promise.resolve({} as any)) : refetch,
  };
};
